import React, { useState, useEffect, useRef } from "react";
import { Youtube, Instagram, CheckCircle2, HelpCircle, Flag, Activity, History, LineChart } from "lucide-react";
import { api } from "@/lib/api";
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from "@/components/ui/tooltip";

const PLAT = [["instagram", "Instagram", Instagram], ["youtube", "YouTube", Youtube]];
const STEPS = [
  { rn: "I", label: "Platform data", icon: "platform" },
  { rn: "II", label: "Account & view verification", icon: "verify", help: true },
  { rn: "III", label: "Fraud analysis", icon: "fraud" },
  { rn: "IV", label: "Verified reporting", icon: "report" },
];

function reduced() {
  return typeof window !== "undefined" && window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

function useCountUp(target, run) {
  const [n, setN] = useState(0);
  useEffect(() => {
    if (target == null) return;
    if (!run || reduced()) { setN(target); return; }
    let raf; const dur = 1200; const start = performance.now();
    const tick = (t) => { const p = Math.min((t - start) / dur, 1); setN(Math.round(target * p)); if (p < 1) raf = requestAnimationFrame(tick); };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, run]);
  return n;
}

export default function VerificationSection() {
  const [stats, setStats] = useState(null);
  const [visible, setVisible] = useState(false);
  const ref = useRef();

  useEffect(() => { api.get("/public/stats").then((r) => setStats(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    if (!ref.current) return;
    const io = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setVisible(true); io.disconnect(); } }, { threshold: 0.2 });
    io.observe(ref.current);
    return () => io.disconnect();
  }, [stats]);

  const count = useCountUp(stats?.number ?? null, visible);
  if (!stats || !stats.show_verification) return null;

  const headline = stats.verification_headline || "The verification layer that protects every campaign";
  const [line1, line2] = splitHeadline(headline);

  const visual = (icon) => {
    if (icon === "platform") return <PlatformCard stats={stats} />;
    if (icon === "verify") return <VerifyVisual />;
    if (icon === "fraud") return <FraudVisual />;
    return <ReportCard stats={stats} count={count} />;
  };

  const Label = ({ s }) => (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6, color: "#fff", fontSize: 14 }}>
      {s.label}
      {s.help && (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <button aria-label="How verification works" className="focus-ring" style={{ background: "rgba(255,255,255,.12)", border: "1px solid rgba(255,255,255,.2)", borderRadius: 999, width: 20, height: 20, color: "#fff", cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <HelpCircle size={12} aria-hidden="true" />
              </button>
            </TooltipTrigger>
            <TooltipContent style={{ maxWidth: 260, background: "var(--raised)", border: "1px solid var(--border-strong)", color: "var(--text)" }}>
              We verify that clippers own the accounts they post from, and we check views against the platform data or our own review instead of relying on what clippers report.
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
    </div>
  );

  return (
    <section aria-labelledby="verif-heading" ref={ref} data-testid="verification-section">
      <div className="brand-gradient" style={{ borderRadius: 24, padding: "56px clamp(20px,4vw,48px) 48px", position: "relative" }}>
        <div style={{ position: "relative", zIndex: 2 }}>
          <h2 id="verif-heading" style={{ textAlign: "center", color: "#fff", fontWeight: 500, fontSize: "clamp(26px,3.4vw,40px)", lineHeight: 1.15, margin: "0 auto", maxWidth: 640 }}>
            {line1}{" "}<br aria-hidden="true" />{line2}
          </h2>

          {/* ===== DESKTOP (>=1024): visuals row w/ connector segments + labels row ===== */}
          <div className="v-desktop">
            <div style={{ position: "relative", marginTop: 48, display: "grid", gridTemplateColumns: "1fr 60px 1fr 60px 1fr 60px 1fr", alignItems: "center", minHeight: 300 }}>
              {STEPS.map((s, i) => (
                <React.Fragment key={s.rn}>
                  <div style={{ display: "flex", justifyContent: "center" }}>{visual(s.icon)}</div>
                  {i < 3 && <div className="dash-line" aria-hidden="true" style={{ width: "100%" }} />}
                </React.Fragment>
              ))}
            </div>
            <div style={{ marginTop: 40, display: "grid", gridTemplateColumns: "1fr 60px 1fr 60px 1fr 60px 1fr" }}>
              {STEPS.map((s, i) => (
                <React.Fragment key={s.rn}>
                  <div style={{ textAlign: "center", padding: "0 10px", borderLeft: i === 0 ? "none" : "1px solid rgba(255,255,255,.14)" }}>
                    <div style={{ fontFamily: "Georgia, 'Times New Roman', serif", fontSize: 20, color: "var(--banner-sub)", whiteSpace: "nowrap" }}>{s.rn}</div>
                    <div style={{ minHeight: 40, display: "flex", alignItems: "center", justifyContent: "center", marginTop: 4 }}><Label s={s} /></div>
                  </div>
                  {i < 3 && <div aria-hidden="true" />}
                </React.Fragment>
              ))}
            </div>
          </div>

          {/* ===== MID (640-1023): 2x2 grid, no line ===== */}
          <div className="v-mid">
            <div style={{ marginTop: 40, display: "grid", gridTemplateColumns: "repeat(2,1fr)", gap: 28 }}>
              {STEPS.map((s) => (
                <div key={s.rn} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 16 }}>
                  <div style={{ minHeight: 170, display: "flex", alignItems: "center", justifyContent: "center", width: "100%" }}>{visual(s.icon)}</div>
                  <div style={{ textAlign: "center" }}>
                    <div style={{ fontFamily: "Georgia, serif", fontSize: 20, color: "var(--banner-sub)" }}>{s.rn}</div>
                    <div style={{ marginTop: 4 }}><Label s={s} /></div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* ===== MOBILE (<640): vertical stack with vertical dashed line ===== */}
          <div className="v-mobile">
            <div style={{ position: "relative", marginTop: 36, paddingLeft: 8 }}>
              <div className="dash-vert" aria-hidden="true" style={{ position: "absolute", left: 22, top: 20, bottom: 20 }} />
              <div style={{ display: "flex", flexDirection: "column", gap: 28 }}>
                {STEPS.map((s) => (
                  <div key={s.rn} style={{ display: "flex", alignItems: "center", gap: 16 }}>
                    <div style={{ width: 100, flexShrink: 0, textAlign: "left" }}>
                      <div style={{ fontFamily: "Georgia, serif", fontSize: 20, color: "var(--banner-sub)" }}>{s.rn}</div>
                      <div style={{ marginTop: 2, display: "flex" }}><Label s={s} /></div>
                    </div>
                    <div style={{ flex: 1, display: "flex", justifyContent: "center" }}>{visual(s.icon)}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .v-desktop, .v-mid, .v-mobile { display: none; }
        @media (min-width: 1024px) { .v-desktop { display: block; } }
        @media (min-width: 640px) and (max-width: 1023px) { .v-mid { display: block; } }
        @media (max-width: 639px) { .v-mobile { display: block; } }
      `}</style>
    </section>
  );
}

const FROST = { background: "rgba(14,16,32,.75)", backdropFilter: "blur(8px)", WebkitBackdropFilter: "blur(8px)", border: "1px solid rgba(255,255,255,.14)", borderRadius: 16 };

function PlatformCard({ stats }) {
  return (
    <div style={{ ...FROST, padding: 14, width: "100%", maxWidth: 220 }}>
      {PLAT.map(([key, name, Icon], idx) => {
        const p = (stats.platforms || []).find((x) => x.platform === key);
        const connected = p?.status === "connected";
        return (
          <div key={key} style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 0", borderTop: idx === 0 ? "none" : "1px solid rgba(255,255,255,.1)" }}>
            <Icon size={20} color="#fff" aria-hidden="true" style={{ flexShrink: 0 }} />
            <div style={{ minWidth: 0 }}>
              <div style={{ color: "#fff", fontSize: 15, fontWeight: 500 }}>{name}</div>
              <div style={{ fontSize: 13, color: connected ? "var(--success)" : "var(--banner-sub)" }}>{connected ? "Connected" : "Manual check"}</div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function VerifyVisual() {
  return (
    <div style={{ position: "relative", width: 130, height: 130, display: "flex", alignItems: "center", justifyContent: "center" }} aria-hidden="true">
      {[0, 1, 2].map((i) => (
        <span key={i} className="pulse-ring" style={{ position: "absolute", width: 60 + i * 24, height: 60 + i * 24, borderRadius: 999, border: "1px solid rgba(255,255,255,.5)", animationDelay: `${i * 0.5}s` }} />
      ))}
      <CheckCircle2 size={38} color="#fff" style={{ position: "relative" }} />
    </div>
  );
}

function FraudVisual() {
  const cards = [["Engagement patterns", Activity, -10, -2], ["Account history", History, 10, 2], ["Creator analytics", LineChart, -10, -2]];
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12, width: "100%", alignItems: "center" }}>
      {cards.map(([label, Icon, dx, rot]) => (
        <div key={label} style={{ ...FROST, height: 56, width: 236, maxWidth: "100%", transform: `translateX(${dx}px) rotate(${rot}deg)`, display: "flex", alignItems: "center", gap: 10, padding: "0 14px" }}>
          <span style={{ width: 20, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center" }}><Icon size={18} color="#fff" aria-hidden="true" /></span>
          <span style={{ color: "#fff", fontSize: 15, fontWeight: 500, whiteSpace: "nowrap" }}>{label}</span>
        </div>
      ))}
    </div>
  );
}

function ReportCard({ stats, count }) {
  const showNumber = stats.display !== "none";
  const caption = stats.display === "verified" ? "Clips verified" : stats.display === "flagged" ? "Flagged for review" : "Every flagged clip is reviewed by our team";
  return (
    <div style={{ ...FROST, padding: 18, width: 240, maxWidth: "100%", textAlign: "center" }}>
      <Flag size={22} color="#fff" aria-hidden="true" style={{ margin: "0 auto 10px" }} />
      {showNumber && <div data-testid="verif-number" style={{ color: "#fff", fontSize: 40, fontWeight: 500, lineHeight: 1 }}>{count}</div>}
      <div style={{ color: showNumber ? "var(--banner-sub)" : "#fff", fontSize: 16, lineHeight: 1.4, marginTop: showNumber ? 8 : 0 }}>{caption}</div>
    </div>
  );
}

function splitHeadline(h) {
  const words = h.split(" ");
  if (words.length < 4) return [h, ""];
  const mid = Math.ceil(words.length / 2);
  return [words.slice(0, mid).join(" "), words.slice(mid).join(" ")];
}
